import React, { useEffect, useRef } from 'react';
import { CovalentBalanceItem } from '@shared/types/frontend';
import { TradeMode } from '@shared/types/frontend/trade';
import { ChainId } from '@shared/utils/config/networks';
import { WrappedNativeTokens } from '@shared/types/quoting-engine/wrappedNativeTokens';
import SlidingModal from '@/components/home/mobile/SlidingModal';
import useParseAssetsIntoNativeAndCash from '@/hooks/asset/useParseAssetsIntoNativeAndCash';
import usePortfolioBalance from '@/hooks/portfolio/usePortfolioBalance';
import { useStableCoinsData } from '@/hooks/asset/useTokenData';
import TokenImage from '@/components/shared/TokenImage';
import { getNetworkLogo, getTokenImageFromAssetItem } from '@/lib/misc/tokenImageHelper';
import { toSpecialFixed } from '@/lib/misc/utils';
import { isNativeTokenAddress } from '@/lib/asset/nativeAddressHelper';
interface Props {
  isOpen: boolean;
  onClose: () => void;
  tradeMode: TradeMode;
  setAsset: (_: CovalentBalanceItem[]) => void;
  tradedAsset: CovalentBalanceItem;
}
export default function BuySellAssetSelectModal({
  isOpen,
  onClose,
  tradeMode,
  setAsset,
  tradedAsset
}: Props) {
  const hasInitialized = useRef(false);
  const {
    data: assetData
  } = usePortfolioBalance({});
  const {
    _nativeNetworkBalances,
    _allCashBalances
  } = useParseAssetsIntoNativeAndCash(assetData, tradedAsset?.network);
  const {
    data: stableCoinsData
  } = useStableCoinsData(tradedAsset?.network);
  const nativeAsset = _nativeNetworkBalances?.[0];
  const stableAsset = tradeMode === TradeMode.BUY ? _allCashBalances?.find(asset => asset.network === tradedAsset?.network && (tradedAsset?.network === ChainId.BSC ? asset.contract_ticker_symbol === 'USDT' : asset.contract_ticker_symbol === 'USDC')) : stableCoinsData?.find(asset => asset.network === tradedAsset?.network && (tradedAsset?.network === ChainId.BSC ? asset.contract_ticker_symbol === 'USDT' : asset.contract_ticker_symbol === 'USDC'));
  useEffect(() => {
    if (!hasInitialized.current) {
      if (nativeAsset && !isTradedAsset(nativeAsset)) {
        setAsset([nativeAsset]);
        hasInitialized.current = true;
      } else if (stableAsset && !isTradedAsset(stableAsset)) {
        setAsset([stableAsset]);
        hasInitialized.current = true;
      }
    }
  }, [nativeAsset, stableAsset]);
  const isTradedAsset = (asset: CovalentBalanceItem) => {
    if (asset?.contract_address?.toLocaleLowerCase() === tradedAsset?.contract_address?.toLocaleLowerCase() || isNativeTokenAddress(asset?.contract_address) && WrappedNativeTokens.get(asset?.network)?.toLocaleLowerCase() === tradedAsset?.contract_address?.toLocaleLowerCase()) {
      return true;
    } else {
      return false;
    }
  };
  return <SlidingModal heightFit isOpen={isOpen} onClose={onClose} data-sentry-element="SlidingModal" data-sentry-component="BuySellAssetSelectModal" data-sentry-source-file="BuySellAssetSelectModal.tsx">
      <div className='flex flex-col items-center gap-3 px-4 pb-6'>
        <div className='text-[16px] font-medium'>
          {tradeMode === TradeMode.BUY ? 'Buy from' : 'Sell to'}
        </div>
        <div className='flex flex-col w-full'>
          {nativeAsset && !isTradedAsset(nativeAsset) ? <div className='flex items-center justify-between py-3' onClick={() => {
          setAsset([nativeAsset]);
          onClose();
        }}>
              <div className='flex items-center justify-center gap-4'>
                <TokenImage mainImageSrc={getTokenImageFromAssetItem(nativeAsset)} badgeImageSrc={getNetworkLogo(nativeAsset.network)} size={32} badgeSize={16} />
                <div className='flex flex-col'>
                  <div className='text-genius-cream text-base font-medium'>
                    {nativeAsset.contract_name}
                  </div>
                  <div className='text-xs text-genius-cream/60'>
                    {nativeAsset.contract_ticker_symbol}
                  </div>
                </div>
              </div>
              {tradeMode === TradeMode.BUY ? <div className='flex flex-col items-end'>
                  <div className='text-genius-cream text-base font-medium'>
                    ${toSpecialFixed(nativeAsset.usd_value).value}
                  </div>
                  <div className='text-xs text-genius-cream/60'>
                    {nativeAsset.formatted_balance?.toFixed(2)} {nativeAsset.contract_ticker_symbol}
                  </div>
                </div> : null}
            </div> : null}
          {stableAsset && !isTradedAsset(stableAsset) ? <div className='flex items-center justify-between py-3' onClick={() => {
          setAsset([stableAsset]);
          onClose();
        }}>
              <div className='flex items-center justify-center gap-4'>
                <TokenImage mainImageSrc={getTokenImageFromAssetItem(stableAsset)} badgeImageSrc={getNetworkLogo(stableAsset.network)} size={32} badgeSize={16} />
                <div className='flex flex-col'>
                  <div className='text-genius-cream text-base font-medium'>
                    {stableAsset.contract_name}
                  </div>
                  <div className='text-xs text-genius-cream/60'>
                    {stableAsset.contract_ticker_symbol}
                  </div>
                </div>
              </div>
              {tradeMode === TradeMode.BUY ? <div className='flex flex-col items-end'>
                  <div className='text-genius-cream text-base font-medium'>
                    ${stableAsset.usd_value ? toSpecialFixed(stableAsset.usd_value).value : '-'}
                  </div>
                  <div className='text-xs text-genius-cream/60'>
                    {stableAsset.formatted_balance?.toFixed(2)} {stableAsset.contract_ticker_symbol}
                  </div>
                </div> : null}
            </div> : null}
          {!(nativeAsset && !isTradedAsset(nativeAsset)) && !(stableAsset && !isTradedAsset(stableAsset)) ? <div className='flex items-center justify-center py-3'>
              <div className='text-genius-cream/60 text-[16px] font-medium'>
                No assets available on this network
              </div>
            </div> : null}
        </div>
      </div>
    </SlidingModal>;
}